import { App } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import { useAuthStore } from '@/stores/auth.store';
import type { ProjectReportComment, ProjectReportRecord } from '@/types/project-report';
import { canCommentProjectReport } from '@/utils/project-report-permissions';

export function useProjectReportDetail(projectReportId: string | undefined) {
  const { message } = App.useApp();
  const currentUser = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ProjectReportRecord | null>(null);
  const [comments, setComments] = useState<ProjectReportComment[]>([]);

  const canComment = canCommentProjectReport(currentUser, report);

  const loadDetail = useCallback(async () => {
    if (!projectReportId) {
      return;
    }

    setLoading(true);
    setReport(null);
    setComments([]);

    try {
      const [detail, commentList] = await Promise.all([
        projectReportsApi.fetchProjectReport(projectReportId),
        projectReportsApi.fetchProjectReportComments(projectReportId),
      ]);
      setReport(detail);
      setComments(commentList);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目日报详情失败';
      message.error(text);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [message, projectReportId]);

  useEffect(() => {
    if (!projectReportId) {
      setReport(null);
      setComments([]);
      setLoading(false);
      return;
    }
    void loadDetail();
  }, [loadDetail, projectReportId]);

  const createComment = async (content: string) => {
    if (!projectReportId) {
      return;
    }

    await projectReportsApi.createProjectReportComment(projectReportId, { content });
    message.success('点评已发布');
    await loadDetail();
  };

  return {
    loading,
    report,
    comments,
    canComment,
    reload: loadDetail,
    createComment,
  };
}
